import { useEffect, useState } from 'react';
import { getQRCode, formatBase64Image } from 'services/qrcodeService';

export interface MemberQRCodeState {
  /** Data URL for the QR image, or '' while loading / on failure. */
  src: string;
  loading: boolean;
  error: boolean;
}

/**
 * Fetches the member QR code from the backend (`common/getqrcode`) instead of drawing it in the
 * browser, mirroring the coupon QR in `CouponAndVoucherDetail`.
 *
 * <p><b>The QR payload is the raw unique id.</b> `common/getqrcode` passes `content` straight into
 * ZXing, so whatever is sent is exactly what a scanner reads back. Base64-encoding it here made
 * scanners read `MDA1OTIxMDAwMTMwODc=` instead of `00592100013087`, which is not what the POS /
 * Juicyapp scanner matches on. The legacy portal Base64'd server-side inside `CommonAPI.GetQRCode`,
 * but that is a detail of the old implementation, not the payload contract — POS reads the id
 * itself (`isRawUniqueNumber`). Sending the raw id keeps the scanned value aligned with the coupon
 * QR, which decodes its Base64 `token` for the same reason.</p>
 *
 * <p>Note the two distinct Base64 layers here: the QR <i>content</i> must be raw, while the QR
 * <i>image</i> returned by the backend stays Base64 and is wrapped into a data URL by
 * `formatBase64Image` so `<img src>` can render it. Do not conflate them.</p>
 *
 * <p>Fires once per distinct `uniqueId`. `Header` lives in the persistent `Layout` route element, so
 * this is one request per session rather than one per navigation. `uniqueId` is the only dependency
 * deliberately — including the i18n `t` function here would refetch on every language switch.</p>
 */
export const useMemberQRCode = (uniqueId?: string): MemberQRCodeState => {
  const [src, setSrc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!uniqueId) {
      setSrc('');
      setError(false);
      setLoading(false);
      // Every path returns a cleanup so the effect has a single return shape.
      return () => undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    const fetchQRCode = async () => {
      try {
        // Base64 the id so the QR carries the legacy payload POS matches on — see the note above.
        const data = await getQRCode(window.btoa(uniqueId));
        if (cancelled) return;
        setSrc(formatBase64Image(data));
      } catch {
        if (cancelled) return;
        setError(true);
        setSrc('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchQRCode();
    return () => {
      cancelled = true;
    };
  }, [uniqueId]);

  return {
    src,
    loading,
    error
  };
};

export default useMemberQRCode;
